import { computeAgeYears, roundToNearest } from './gvMath';
import { MANUFACTURERS, DEPRECIATION, ADJUSTMENTS } from '../config/reserveConfig';

const ageDepreciationEur = (ageYears, usedBaselineEur, cfg) => {
  const { phase1_years, phase1_power, transition_value_eur, phase2_end_years, phase2_power, floor_value_eur } = cfg;

  if (ageYears <= 0) return usedBaselineEur;
  if (ageYears >= phase2_end_years) return floor_value_eur;

  if (ageYears <= phase1_years) {
    const t = ageYears / phase1_years;
    const decay = Math.pow(1 - t, phase1_power);
    return transition_value_eur + (usedBaselineEur - transition_value_eur) * decay;
  }

  // Phase 2: accelerating decline toward floor
  const t = (ageYears - phase1_years) / (phase2_end_years - phase1_years);
  const decay = Math.pow(1 - t, phase2_power);
  return floor_value_eur + (transition_value_eur - floor_value_eur) * decay;
};

const rideFactor = (rides, cfg) => {
  if (rides <= 0) return 1.0;
  const factor = 1.0 - cfg.first_ride_penalty - cfg.per_ride_pct * rides;
  return Math.max(cfg.floor, factor);
};

export const calculateReserve = ({ manufacturer, model, domYear, sizeSqft, rides, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let mfrCfg = null;
  let brandKey = null;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    brandKey = null;
    notes.push('Custom price used. Brand and version adjustments not applied.');
  } else {
    const mfrKey = manufacturer.toLowerCase().trim();
    const mdlKey = model.toLowerCase().trim();
    mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
    const modelCfg = mfrCfg.models[mdlKey];
    if (!modelCfg) {
      throw new Error(`Unknown model: ${model}. Expected: ${Object.keys(mfrCfg.models).join(', ')}`);
    }
    newPriceEur = modelCfg.new_price_eur;
    brandKey = mfrCfg.brand_key;
  }

  // Initial used factor
  const initialUsedFactor = DEPRECIATION.initial_used_factor;
  const usedBaselineEur = newPriceEur * initialUsedFactor;

  // Age depreciation
  const ageCfg = DEPRECIATION.age;
  const ageYears = computeAgeYears(domYear);
  const ageValueEur = ageDepreciationEur(ageYears, usedBaselineEur, ageCfg);
  const ageFactor = newPriceEur > 0 ? ageValueEur / newPriceEur : 0;
  adjustments.ageFactor = Math.round(ageFactor * 1000) / 1000;

  // Ride deduction (multiplicative)
  const rideCfg = DEPRECIATION.rides;
  const actualRides = rides != null ? rides : 0;
  if (rides == null) {
    notes.push('Rides unknown. Assuming 0 deployments.');
  }
  const rFactor = rideFactor(actualRides, rideCfg);
  if (rFactor !== 1.0) {
    adjustments.rideFactor = Math.round(rFactor * 1000) / 1000;
  }

  // Combine: age sets base value, rides multiply it down
  let valueEur = ageValueEur * rFactor;

  // Brand attractiveness
  let brandMult = 1.0;
  if (customNewPriceEur) {
    brandMult = 1.0;
  } else if (brandKey) {
    const brandAdj = ADJUSTMENTS.brand_attractiveness;
    const mfrAdj = brandAdj[brandKey] || {};
    const mdlKey = model.toLowerCase().trim();
    brandMult = mfrAdj[mdlKey] ?? mfrAdj._default ?? brandAdj._default_manufacturer;
  } else {
    brandMult = ADJUSTMENTS.brand_attractiveness._unknown_manufacturer;
    notes.push('Unknown brand - 5% desirability penalty applied.');
  }
  if (brandMult !== 1.0) {
    adjustments.brandAttractiveness = brandMult;
  }

  // Size adjustment
  const sizeAdj = ADJUSTMENTS.size;
  let sizeMult = 1.0;
  if (sizeSqft != null) {
    if (sizeSqft < sizeAdj.small_threshold_sqft) {
      sizeMult = sizeAdj.small_penalty;
      notes.push(`Small reserve (${sizeSqft} sqft < ${sizeAdj.small_threshold_sqft}). Penalty ${Math.round((1 - sizeMult) * 100)}%.`);
    } else if (sizeSqft > sizeAdj.large_threshold_sqft) {
      sizeMult = sizeAdj.large_penalty;
      notes.push(`Large reserve (${sizeSqft} sqft > ${sizeAdj.large_threshold_sqft}). Penalty ${Math.round((1 - sizeMult) * 100)}%.`);
    }
  }
  if (sizeMult !== 1.0) {
    adjustments.size = sizeMult;
  }

  // Final value
  const totalAdjustment = brandMult * sizeMult;
  valueEur = valueEur * totalAdjustment;

  // Round to nearest 10, enforce floor
  valueEur = roundToNearest(valueEur, 10);
  valueEur = Math.max(valueEur, ageCfg.floor_value_eur);

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  return {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };
};
